/**
 * Pure presentation helpers for {@link DynamicChartsComponent}.
 *
 * Extracted from the component to keep the (size-ratcheted) component lean.
 * Nothing here touches Angular or component state - inputs in, values out.
 */

/**
 * Golden-angle hue step (~137.5°) — spreads categorical swatches around the wheel so
 * consecutive series (especially the first 3–5) land in clearly different hue families.
 */
const GOLDEN_ANGLE = 137.508;

/** Alternating lightness nudges relative to the primary anchor (not fixed absolutes). */
const LIGHTNESS_CYCLE = [
    10, -14, 12, -16, 8, -12, 14, -18, 6, -10, 16, -20, 4, -8, 18, -22, 2, -6, 15, -17,
    11, -13, 9, -15, 13, -19, 7, -11, 17, -21,
];
const SATURATION_CYCLE = [
    1.02, 0.98, 1.04, 0.96, 1.03, 0.97, 1.05, 0.95, 1.02, 0.98, 1.04, 0.96, 1.03, 0.97, 1.05,
    0.95, 1.02, 0.98, 1.04, 0.96, 1.03, 0.97, 1.05, 0.95, 1.02, 0.98, 1.04, 0.96, 1.03, 0.97,
];

const rgbToHsl = (
    r0: number,
    g0: number,
    b0: number,
): [number, number, number] => {
    r0 /= 255;
    g0 /= 255;
    b0 /= 255;
    const max = Math.max(r0, g0, b0),
        min = Math.min(r0, g0, b0);
    let h = 0,
        s = 0,
        l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r0:
                h = (g0 - b0) / d + (g0 < b0 ? 6 : 0);
                break;
            case g0:
                h = (b0 - r0) / d + 2;
                break;
            case b0:
                h = (r0 - g0) / d + 4;
                break;
        }
        h *= 60;
    }
    return [h, s * 100, l * 100];
};

const hslToRgb = (
    h: number,
    s: number,
    l: number,
): [number, number, number] => {
    h /= 360;
    s /= 100;
    l /= 100;
    const hueToRgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    let r1: number, g1: number, b1: number;
    if (s === 0) {
        r1 = g1 = b1 = l;
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r1 = hueToRgb(p, q, h + 1 / 3);
        g1 = hueToRgb(p, q, h);
        b1 = hueToRgb(p, q, h - 1 / 3);
    }
    return [
        Math.round(r1 * 255),
        Math.round(g1 * 255),
        Math.round(b1 * 255),
    ];
};

const hslToHex = (h: number, s: number, l: number): string => {
    const [nr, ng, nb] = hslToRgb((h + 360) % 360, s, l);
    return `#${((1 << 24) + (nr << 16) + (ng << 8) + nb).toString(16).slice(1)}`;
};

const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value));

/**
 * Builds the chart colour list for a given base colour.
 *
 * The primary leads unchanged; every further swatch walks the hue wheel by the golden
 * angle (~137.5° per slot) with alternating lightness/saturation nudges anchored to
 * the primary so multi-line / multi-bar series stay easy to tell apart.
 */
export function generateColorVariants(
    color: string,
    steps: number = 20,
): string[] {
    const originalColor = color.startsWith('#') ? color : `#${color}`;
    const normalized = originalColor.replace('#', '');
    const num = parseInt(normalized, 16);
    const [baseHue, baseSat, baseLight] = rgbToHsl(
        num >> 16,
        (num >> 8) & 0xff,
        num & 0xff,
    );

    const colors = [originalColor];
    const used = new Set<string>([originalColor.toLowerCase()]);

    const pushVariant = (
        hueOffset: number,
        lightDelta: number,
        satFactor: number,
    ): boolean => {
        let sat = clamp(baseSat * satFactor, 48, 84);
        let light = clamp(baseLight + lightDelta, 38, 72);
        let hue = (baseHue + hueOffset + 360) % 360;

        for (let attempt = 0; attempt < 14; attempt++) {
            const hex = hslToHex(hue, sat, light);
            if (!used.has(hex)) {
                colors.push(hex);
                used.add(hex);
                return true;
            }

            if (attempt % 2 === 0) {
                light = clamp(light + 3, 38, 72);
            } else {
                light = clamp(light - 3, 38, 72);
            }
            hue = (hue + (attempt % 2 === 0 ? 1.5 : -1.5) + 360) % 360;
            sat = clamp(sat + (attempt % 3 === 0 ? 1 : -1), 48, 84);
        }

        return false;
    };

    let slot = 0;
    while (colors.length < steps && slot < steps * 3) {
        const hueOffset = (slot + 1) * GOLDEN_ANGLE;
        pushVariant(
            hueOffset,
            LIGHTNESS_CYCLE[slot % LIGHTNESS_CYCLE.length],
            SATURATION_CYCLE[slot % SATURATION_CYCLE.length],
        );
        slot++;
    }

    return colors;
}

/**
 * Collapses a pie series to its top-N slices, folding the remainder into an
 * "Others" slice so dense pies stay readable.
 */
export function aggregatePieData(
    labels: any[],
    values: number[],
    topN: number = 10,
): { labels: any[]; series: number[] } {
    const pairs = labels.map((label, i) => ({
        label,
        value: Number(values[i]) || 0,
    }));

    // Sort DESC
    pairs.sort((a, b) => b.value - a.value);

    const top = pairs.slice(0, topN);
    const rest = pairs.slice(topN);

    const othersSum = rest.reduce((sum, p) => sum + p.value, 0);

    const finalLabels = top.map(p => p.label);
    const finalSeries = top.map(p => p.value);

    if (othersSum > 0) {
        finalLabels.push('Others');
        finalSeries.push(othersSum);
    }

    return { labels: finalLabels, series: finalSeries };
}

export interface SeriesValueRow {
    name?: string;
    data?: Array<number | null | string>;
}

export type SeriesMapLike = Record<string, SeriesValueRow[]>;

/**
 * Returns a namer for grouped series legend/tooltip labels.
 *
 * - Single selected group: the metric name is used as-is.
 * - Multiple groups: the group key already identifies each series, so the
 *   metric (e.g. "- total_net_sales") is appended only when a single group
 *   yields more than one series (multiple metrics) and the metric is needed to
 *   tell them apart ("multi-grouping"). With one entry per group the key alone
 *   is shown (no redundant "- total_net_sales" suffix).
 */
export function makeSeriesNamer(
    selectedKeys: string[],
    seriesMap: SeriesMapLike,
): (key: string, entryName: string | undefined, idx: number) => string {
    const prefixKey = selectedKeys.length > 1;
    const showMetric = selectedKeys.some(
        key => (seriesMap[key] ?? []).length > 1,
    );
    return (key, entryName, idx) => {
        const metric = entryName || `Series ${idx}`;
        if (!prefixKey) {
            return metric;
        }
        return showMetric ? `${key} - ${metric}` : key;
    };
}

/** Sum of finite numeric values across all points in all series entries for one key. */
function sumSeriesKeyValue(entries: SeriesValueRow[] | undefined): number {
    if (!entries?.length) {
        return 0;
    }
    let sum = 0;
    for (const entry of entries) {
        const data = entry?.data;
        if (!Array.isArray(data)) {
            continue;
        }
        for (const v of data) {
            const n = Number(v);
            if (Number.isFinite(n)) {
                sum += n;
            }
        }
    }
    return sum;
}

/**
 * Default group-by / multi-series selection when nothing is saved yet:
 * fewer than `maxCount` options → all keys (original order);
 * otherwise → top `maxCount` keys by total series value (desc), ties keep input order.
 */
export function resolveDefaultSeriesSelection(
    visibleKeys: string[],
    seriesMap: SeriesMapLike,
    maxCount: number = 10,
): string[] {
    if (!visibleKeys.length) {
        return [];
    }
    if (visibleKeys.length < maxCount) {
        return [...visibleKeys];
    }

    const ranked = visibleKeys.map((key, index) => ({
        key,
        index,
        value: sumSeriesKeyValue(seriesMap[key]),
    }));

    ranked.sort((a, b) => {
        if (b.value !== a.value) {
            return b.value - a.value;
        }
        return a.index - b.index;
    });

    return ranked.slice(0, maxCount).map(r => r.key);
}

/** "net_sales_sar" -> "Net Sales Sar"; blank in -> blank out. */
export function prettifyColumnName(name: string | undefined | null): string {
    if (!name) {
        return '';
    }
    return String(name)
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

/** Split on top-level commas only (commas inside parentheses are ignored). */
function splitTopLevelCommas(input: string): string[] {
    const out: string[] = [];
    let depth = 0;
    let cur = '';
    for (const ch of input) {
        if (ch === '(') {
            depth++;
            cur += ch;
        } else if (ch === ')') {
            depth = Math.max(0, depth - 1);
            cur += ch;
        } else if (ch === ',' && depth === 0) {
            out.push(cur);
            cur = '';
        } else {
            cur += ch;
        }
    }
    if (cur.trim()) {
        out.push(cur);
    }
    return out;
}

/**
 * Best-effort fallback: pull the grouping (non-date) column out of a chart's
 * SQL `GROUP BY`. Used only when the backend payload lacks `groupBy` (charts
 * persisted before it was surfaced). Fragile by design - returns `null` unless a
 * single plain dimension column can be identified.
 */
export function deriveGroupByFromQuery(
    query?: string | null,
    dateColumn?: string | null,
): string | null {
    if (!query || typeof query !== 'string') {
        return null;
    }
    const match =
        /group\s+by\s+([\s\S]+?)(?:\border\s+by\b|\bhaving\b|\blimit\b|\bwindow\b|\)\s|;|$)/i.exec(
            query,
        );
    if (!match) {
        return null;
    }
    const dateLike =
        /(to_char|to_date|date_trunc|date_part|datepart|extract|trunc|year|month|quarter|week|day)\s*\(/i;
    const dateCol = dateColumn ? String(dateColumn).toLowerCase() : '';
    for (const raw of splitTopLevelCommas(match[1])) {
        const col = raw.trim();
        if (!col || dateLike.test(col)) {
            continue;
        }
        const bare = col.replace(/^[\w]+\./, '').replace(/["'`]/g, '');
        if (!/^[\w.]+$/.test(col.replace(/["'`]/g, ''))) {
            continue; // not a plain identifier (skip expressions/positions)
        }
        if (dateCol && bare.toLowerCase() === dateCol) {
            continue; // the date column itself
        }
        return bare;
    }
    return null;
}

/** Month name (full or 3-letter abbreviation, case-insensitive) -> 0..11. */
const MONTH_INDEX: Record<string, number> = (() => {
    const names = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december',
    ];
    const lookup: Record<string, number> = {};
    names.forEach((name, i) => {
        lookup[name] = i;
        lookup[name.slice(0, 3)] = i;
    });
    return lookup;
})();

/**
 * If every label is a month name (full or abbreviated), returns the index
 * permutation that puts them in calendar order; otherwise `null` (leave as-is).
 * Returns `null` too when the labels are already chronological.
 */
function chronologicalMonthOrder(labels: any[]): number[] | null {
    if (!Array.isArray(labels) || labels.length < 2) {
        return null;
    }
    const monthIdx = labels.map(l =>
        l == null ? -1 : MONTH_INDEX[String(l).trim().toLowerCase()] ?? -1,
    );
    if (monthIdx.some(i => i < 0)) {
        return null; // not a pure month axis - do not reorder
    }
    const order = labels.map((_, i) => i).sort((a, b) => monthIdx[a] - monthIdx[b]);
    return order.every((v, i) => v === i) ? null : order;
}

/**
 * Safeguard for time-series charts whose month labels arrive out of order
 * (e.g. a backend that alphabetically sorted full month names). When every
 * label is a month, reorders `labels` and every aligned series `data` array
 * into calendar order; otherwise returns the input unchanged (same reference).
 */
export function reorderChartDataByMonth<
    T extends { labels?: any[]; series?: unknown },
>(chartData: T): T {
    const labels = chartData?.labels;
    const series = chartData?.series;
    if (!Array.isArray(labels) || !series || typeof series !== 'object') {
        return chartData;
    }
    const order = chronologicalMonthOrder(labels);
    if (!order) {
        return chartData;
    }
    const seriesMap = series as SeriesMapLike;
    const nextSeries: Record<string, unknown> = {};
    for (const key of Object.keys(seriesMap)) {
        const entries = seriesMap[key];
        nextSeries[key] = Array.isArray(entries)
            ? entries.map(entry =>
                  entry && Array.isArray(entry.data)
                      ? { ...entry, data: order.map(i => entry.data![i]) }
                      : entry,
              )
            : entries;
    }
    return {
        ...chartData,
        labels: order.map(i => labels[i]),
        series: nextSeries,
    };
}

/**
 * Numeric Y for a tracker threshold annotation, or `null` when there is no
 * threshold to draw (missing / empty / `"none"` type or value, or non-finite).
 */
export function resolveTrackerThreshold(
    threshold: number | string | null | undefined,
    thresholdType?: string | null,
): number | null {
    const type = String(thresholdType ?? '')
        .trim()
        .toLowerCase();
    if (!type || type === 'none') {
        return null;
    }
    if (threshold == null || threshold === '') {
        return null;
    }
    const raw = String(threshold).trim().toLowerCase();
    if (!raw || raw === 'none') {
        return null;
    }
    const value = Number(threshold);
    return Number.isFinite(value) ? value : null;
}

// ---------- Shared multi-series tooltip ----------

/** One rendered series line inside the shared line/bar tooltip. */
export interface TooltipSeriesRow {
    name: string;
    value: string;
    color: string;
}

/** Column layout chosen for a shared tooltip so that it fits inside the plot. */
export interface TooltipLayout {
    columns: number;
    /** Rows that fit; anything past this is summarised as "+N more". */
    maxRows: number;
    /** Estimated rendered height in px. */
    height: number;
}

const TOOLTIP_ROW_HEIGHT = 22;
/** Title row + vertical padding + border. */
const TOOLTIP_CHROME_HEIGHT = 56;
/** Clearance kept between the tooltip and the plot edges. */
const TOOLTIP_PLOT_CLEARANCE = 40;
const TOOLTIP_MIN_ROWS_PER_COLUMN = 4;
const TOOLTIP_MAX_ROWS_PER_COLUMN = 11;

/**
 * Splits a shared tooltip into as many columns as it takes to keep its height
 * inside the plot.
 *
 * ApexCharts never clamps a `followCursor` tooltip to the grid (its
 * `Position.js` overwrites the clamped coordinates with raw cursor ones), so a
 * tooltip taller than the chart overhangs the card, grows the scrollable area
 * of the nearest scrolling ancestor, and the scrollbar that appears resizes the
 * chart - which re-runs the hover hit-test and re-renders the tooltip. That
 * feedback loop is what reads as the tooltip flickering under the pointer on
 * many-series charts. Bounding the height stops the loop at the source.
 */
export function resolveTooltipLayout(
    rowCount: number,
    chartHeight: number,
    narrow: boolean,
): TooltipLayout {
    const usable =
        (chartHeight || 400) - TOOLTIP_CHROME_HEIGHT - TOOLTIP_PLOT_CLEARANCE;
    const rowsPerColumn = Math.min(
        TOOLTIP_MAX_ROWS_PER_COLUMN,
        Math.max(
            TOOLTIP_MIN_ROWS_PER_COLUMN,
            Math.floor(usable / TOOLTIP_ROW_HEIGHT),
        ),
    );
    const rows = Math.max(rowCount, 1);
    const columns = Math.min(
        narrow ? 2 : 3,
        Math.max(1, Math.ceil(rows / rowsPerColumn)),
    );
    const maxRows = rowsPerColumn * columns;
    return {
        columns,
        maxRows,
        height:
            TOOLTIP_CHROME_HEIGHT +
            Math.ceil(Math.min(rows, maxRows) / columns) * TOOLTIP_ROW_HEIGHT,
    };
}

/**
 * `true` while the tooltip is short enough to keep chasing the pointer.
 *
 * ApexCharts' `followCursor` branch flips the tooltip by its own full
 * width/height the moment the pointer crosses the plot mid-line, so a tall
 * tooltip teleports much further than the pointer moved. Past half the plot
 * height it is anchored to the hovered point instead - the one path ApexCharts
 * actually clamps to the grid.
 */
export function shouldFollowTooltipCursor(
    rowCount: number,
    chartHeight: number,
    narrow: boolean,
): boolean {
    const { height } = resolveTooltipLayout(rowCount, chartHeight, narrow);
    return height <= (chartHeight || 400) / 2;
}

interface TooltipSeriesLike {
    name?: string;
    color?: string;
    data?: Array<number | string | null | undefined>;
}

/** The ApexCharts `w` handed to a `tooltip.custom` callback (narrowed). */
export interface TooltipRenderContext {
    config?: { series?: TooltipSeriesLike[]; colors?: string[] };
    globals?: { colors?: string[] };
}

/**
 * Tooltip rows for one hovered point. Series whose value is blank / NaN / zero
 * are dropped - on a many-series chart they are noise - unless `keepZeros` is
 * set, which is the case for the aggregated "others" bucket where a zero is
 * itself the answer.
 */
export function collectTooltipRows(
    w: TooltipRenderContext,
    dataPointIndex: number,
    keepZeros: boolean,
    formatValue: (value: number) => string,
): TooltipSeriesRow[] {
    const rows: TooltipSeriesRow[] = [];
    (w.config?.series ?? []).forEach((entry, idx) => {
        const value = entry?.data?.[dataPointIndex];
        const isBlank = value === undefined || value === null || value === '';
        const isNaNLike =
            (typeof value === 'number' && Number.isNaN(value)) ||
            (typeof value === 'string' && value.trim() === 'nan');
        if (!keepZeros && (isBlank || isNaNLike || value === 0)) {
            return;
        }
        rows.push({
            name: entry?.name || `Series ${idx + 1}`,
            value:
                isBlank || isNaNLike
                    ? '0.00'
                    : typeof value === 'string'
                      ? value
                      : formatValue(value as number),
            color:
                w.globals?.colors?.[idx] ||
                w.config?.colors?.[idx] ||
                entry?.color ||
                'currentColor',
        });
    });
    return rows;
}

const HTML_ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

/** Series names and values come from backend data - never inject them as markup. */
function escapeHtml(value: string): string {
    return String(value).replace(/[&<>"']/g, char => HTML_ESCAPES[char]);
}

/**
 * Reorders cells so that a row-major CSS grid reads down each column in series
 * order - which keeps grouped series ("CPD - ...", then "FSD - ...") together
 * instead of interleaving them across columns. `null` is an alignment spacer.
 */
function toColumnMajorCells(
    rows: TooltipSeriesRow[],
    columns: number,
): Array<TooltipSeriesRow | null> {
    if (columns <= 1) {
        return rows;
    }
    const perColumn = Math.ceil(rows.length / columns);
    const cells: Array<TooltipSeriesRow | null> = [];
    for (let row = 0; row < perColumn; row++) {
        for (let column = 0; column < columns; column++) {
            cells.push(rows[column * perColumn + row] ?? null);
        }
    }
    while (cells.length && cells[cells.length - 1] === null) {
        cells.pop();
    }
    return cells;
}

/**
 * Markup for the shared line/bar tooltip. Only the series swatch is inline;
 * everything else is the `.sai-chart-tooltip` recipe in
 * `dynamic-charts.component.scss`, so the tooltip follows the design tokens and
 * flips with the colour scheme.
 */
export function buildSharedTooltipHtml(
    title: string,
    rows: TooltipSeriesRow[],
    layout: TooltipLayout,
    text: { empty: string; more: (hidden: number) => string },
): string {
    const shown = rows.slice(0, layout.maxRows);
    const hidden = rows.length - shown.length;
    const cells = toColumnMajorCells(shown, layout.columns)
        .map((cell) => {
            if (!cell) {
                return '<div class="sai-chart-tooltip__row"></div>';
            }
            const swatch = `<span class="sai-chart-tooltip__dot" style="background:${escapeHtml(cell.color)}"></span>`;
            const name = `<span class="sai-chart-tooltip__name">${escapeHtml(cell.name)}</span>`;
            const value = `<span class="sai-chart-tooltip__value">${escapeHtml(cell.value)}</span>`;
            return `<div class="sai-chart-tooltip__row">${swatch}${name}${value}</div>`;
        })
        .join('');
    const body = shown.length
        ? `<div class="sai-chart-tooltip__grid" style="--sai-chart-tooltip-columns:${layout.columns}">${cells}</div>`
        : `<div class="sai-chart-tooltip__empty">${escapeHtml(text.empty)}</div>`;
    const more = hidden > 0
        ? `<div class="sai-chart-tooltip__more">${escapeHtml(text.more(hidden))}</div>`
        : '';
    const heading = `<div class="sai-chart-tooltip__title">${escapeHtml(title)}</div>`;
    return `<div class="sai-chart-tooltip">${heading}${body}${more}</div>`;
}

/** Yield to scroll/input; run heavy chart work when the main thread is idle. */
export function scheduleWhenIdle(
    cb: () => void,
    timeoutMs = 600,
): void {
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(cb, { timeout: timeoutMs });
    } else {
        setTimeout(cb, 48);
    }
}

/** Max category points rendered on tracker cards — keeps Apex init off the hot path. */
export const TRACKER_DISPLAY_MAX_POINTS = 64;

/**
 * Uniformly decimate labels + every series to `maxPoints` for tracker list view.
 * Full fidelity is preserved in `chartData` for expand / data-table dialogs.
 */
export function downsampleChartData<T extends { data?: Array<number | null> }>(
    labels: unknown[],
    seriesMap: Record<string, T[]>,
    maxPoints: number,
): { labels: unknown[]; seriesMap: Record<string, T[]> } {
    if (!labels.length || labels.length <= maxPoints) {
        return { labels, seriesMap };
    }
    const indices: number[] = [];
    const step = labels.length / maxPoints;
    for (let i = 0; i < maxPoints; i++) {
        indices.push(Math.min(labels.length - 1, Math.floor(i * step)));
    }
    const picked = [...new Set(indices)];
    const newLabels = picked.map(i => labels[i]);
    const newMap: Record<string, T[]> = {};
    for (const [key, entries] of Object.entries(seriesMap)) {
        newMap[key] = entries.map(entry => ({
            ...entry,
            data: Array.isArray(entry.data)
                ? picked.map(i => entry.data![i] ?? null)
                : entry.data,
        }));
    }
    return { labels: newLabels, seriesMap: newMap };
}

/** Cheap memo key — avoids JSON.stringify on large tracker payloads. */
export function trackerChartMemoFingerprint(
    id: string,
    labels: unknown[],
    series: unknown,
    color?: string,
): string {
    let pointCount = 0;
    if (series && typeof series === 'object') {
        for (const arr of Object.values(series as Record<string, unknown>)) {
            if (Array.isArray(arr) && (arr[0] as { data?: unknown[] } | undefined)?.data) {
                pointCount += (arr[0] as { data: unknown[] }).data.length;
            }
        }
    }
    return `${id}::${labels.length}::${pointCount}::${color ?? ''}`;
}
